import React from 'react';
import { useToastStore } from '../../stores/useToastStore';
import { AlertType, Toast } from '../../types';
import { useUIStateStore } from '../../stores/useUIStateStore';
import { useAuth } from '../../contexts/AuthContext';

const getToastStyles = (type: AlertType) => {
  switch (type) {
    case 'success':
      return {
        wrapper: 'border-success/30 bg-[rgb(var(--color-stats-container-bg-rgb))] shadow-success/10',
        icon: 'text-success bg-success/10',
        title: 'text-success',
        bloom: 'bg-success/5'
      };
    case 'error':
      return {
        wrapper: 'border-danger/30 bg-[rgb(var(--color-stats-container-bg-rgb))] shadow-danger/10',
        icon: 'text-danger bg-danger/10',
        title: 'text-danger',
        bloom: 'bg-danger/5'
      };
    case 'warning':
      return {
        wrapper: 'border-warning/30 bg-[rgb(var(--color-stats-container-bg-rgb))] shadow-warning/10',
        icon: 'text-warning bg-warning/10',
        title: 'text-warning',
        bloom: 'bg-warning/5'
      };
    case 'info':
      return {
        wrapper: 'border-info/30 bg-[rgb(var(--color-stats-container-bg-rgb))] shadow-info/10',
        icon: 'text-info bg-info/10',
        title: 'text-info',
        bloom: 'bg-info/5'
      };
    case 'institutional':
      return {
        wrapper: 'border-theme-primary/40 bg-theme-bg-surface/90 backdrop-blur-2xl shadow-theme-primary/10',
        icon: 'text-theme-primary bg-theme-primary/10',
        title: 'text-theme-primary',
        bloom: 'bg-theme-primary/5'
      };
    default:
      return {
        wrapper: 'border-theme-divider bg-[rgb(var(--color-stats-container-bg-rgb))] shadow-sm',
        icon: 'text-theme-text-secondary bg-theme-bg-secondary',
        title: 'text-theme-text-primary',
        bloom: 'bg-transparent'
      };
  }
};

const getIcon = (type: AlertType) => {
  switch (type) {
    case 'success':
      return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'error':
      return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
    case 'warning':
      return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      );
    case 'info':
    case 'institutional':
    default:
      return (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      );
  }
};

const ToastMessage: React.FC<{ toast: Toast & { duration?: number }; onRemove: (id: string) => void }> = ({
  toast,
  onRemove,
}) => {
  const isInstitutional = toast.type === 'institutional';

  // Calculate exit delay: total duration - exit animation duration (2.5s)
  const exitDelay = ((toast.duration || (isInstitutional ? 15000 : 5000)) - 2500) / 1000;

  const styles = getToastStyles(toast.type);

  return (
    <div
      className={`relative flex items-start p-4 rounded-xl shadow-2xl border pointer-events-auto transition-all duration-500 ease-[cubic-bezier(0.23,1,0.32,1)] overflow-hidden ${styles.wrapper} ${isInstitutional
        ? 'w-[92vw] sm:w-[85vw] md:w-max md:max-w-[75vw] lg:max-w-5xl xl:max-w-6xl'
        : 'w-full max-w-sm animate-fade-in-right'
        }`}
      style={isInstitutional ? {
        animation: `fade-in-down-slow 1.2s cubic-bezier(0.23, 1, 0.32, 1) forwards, fade-out-slow 2.5s cubic-bezier(0.23, 1, 0.32, 1) ${exitDelay}s forwards`
      } : undefined}
    >
      {/* Decorative Bloom */}
      <div className={`absolute -right-4 -bottom-4 w-24 h-24 blur-2xl rounded-full ${styles.bloom} pointer-events-none`} />

      {/* Icon */}
      <div className={`flex-shrink-0 mr-4 mt-0.5 p-1.5 rounded-lg backdrop-blur-sm ${styles.icon}`}>
         {getIcon(toast.type)}
      </div>

      <div className="flex-1 overflow-hidden relative z-10">
        {isInstitutional && (
          <div className="flex items-center gap-2 mb-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-theme-primary animate-pulse" />
            <span className="text-xs font-bold text-theme-primary uppercase tracking-wider">System Broadcast</span>
          </div>
        )}
        {!isInstitutional && (
          <div className={`text-xs font-bold uppercase tracking-wider mb-1 ${styles.title}`}>
             {toast.type}
          </div>
        )}
        <div className={`text-sm font-medium leading-relaxed text-theme-text-primary`}>
          {toast.message}
        </div>
        {toast.action && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              toast.action?.onClick();
              onRemove(toast.id);
            }}
            className="mt-3 text-xs font-bold uppercase tracking-wider text-theme-primary hover:text-theme-text-primary transition-colors flex items-center gap-1"
          >
            {toast.action.label}
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </button>
        )}
      </div>
      <button
        onClick={() => onRemove(toast.id)}
        className={`ml-4 flex-shrink-0 transition-all duration-300 hover:scale-125 active:scale-90 text-theme-text-secondary hover:text-theme-text-primary relative z-10`}
        aria-label="Close"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="3"
            d="M6 18L18 6M6 6l12 12"
          ></path>
        </svg>
      </button>
      {isInstitutional && (
        <div className="absolute inset-0 border border-white/5 pointer-events-none rounded-xl" />
      )}
    </div>
  );
};

const ToastContainer: React.FC = () => {
  const { toasts, removeToast } = useToastStore();
  const { user } = useAuth();
  const {
    isDesktopSidebarCollapsed,
    isSidebarHovered,
    sidebarWidth
  } = useUIStateStore();

  const institutionalToasts = toasts.filter((t: Toast) => t.type === 'institutional');
  const standardToasts = toasts.filter((t: Toast) => t.type !== 'institutional');

  // Calculate sidebar offset for desktop only
  // We only apply this if the user is authenticated (sidebar is showing)
  const isVisualExpanded = !isDesktopSidebarCollapsed || isSidebarHovered;

  // Responsive sidebar width: on mobile, sidebar is an overlay or hidden, so offset is 0
  const isMobile = window.innerWidth < 1024; // Tailwind lg breakpoint
  const currentSidebarWidth = (user && !isMobile) ? (isVisualExpanded ? sidebarWidth : 80) : 0;

  return (
    <>
      {/* Institutional/Page Context Notifications - Centered in Content Area */}
      <div
        className="fixed top-4 md:top-8 left-0 right-0 z-[10000] flex flex-col items-center space-y-3 pointer-events-none transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]"
        style={{
          paddingLeft: `calc(${currentSidebarWidth}px + 1rem)`,
          paddingRight: '1rem'
        }}
      >
        {institutionalToasts.map((toast: Toast) => (
          <ToastMessage key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </div>

      {/* Standard Operations Notifications - Top Right */}
      <div className="fixed top-5 right-5 z-[9999] w-full max-w-sm space-y-3 pointer-events-none">
        {standardToasts.map((toast: Toast) => (
          <ToastMessage key={toast.id} toast={toast} onRemove={removeToast} />
        ))}
      </div>

      <style>{`
        @keyframes fade-in-right {
          from {
            opacity: 0;
            transform: translateX(100%);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes fade-in-down-slow {
          0% {
            opacity: 0;
            transform: translateY(-40px) scale(0.95);
            filter: blur(8px);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: blur(0);
          }
        }
        @keyframes fade-out-slow {
          0% {
            opacity: 1;
            transform: translateY(0) scale(1);
            filter: blur(0);
          }
          100% {
            opacity: 0;
            transform: translateY(-20px) scale(0.98);
            filter: blur(12px);
          }
        }
        .animate-fade-in-right {
          animation: fade-in-right 0.4s cubic-bezier(0.23, 1, 0.32, 1) forwards;
        }
        .animate-fade-in-down-slow {
          animation: fade-in-down-slow 1.2s cubic-bezier(0.23, 1, 0.32, 1) forwards;
        }
      `}</style>
    </>
  );
};

export default ToastContainer;
